import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import {
  SMPArrowLeftIcon,
  SMPArrowRightIcon,
  SMPFlex,
  SMPKnockoutMobileContainer,
  SMPKnockoutMobileGameContainer,
  SMPKnockoutSmallFinal,
  SMPText,
} from '../../../../index';
import { INormalizedKnockoutGroup, IPlayoffRoundGroup } from '../../../../../types';
import { KnockoutHead } from './KnockoutHead';
import { KnockoutGame } from './KnockoutGame';
import { KnockoutRoundConnectingLine } from './KnockoutRoundConnectingLine';
import { SMALL_FINAL } from '../../../../../constants/playoff';
import { isKnouckoutGameDoubles } from '@sportal365/smp-widget-utils';
import { renderKnockoutGameType } from './KnockoutGameType';

const arrowCss = {
  width: '10px',
  zIndex: 2,
  '&:hover': {
    color: 'orange',
    cursor: 'pointer',
  },
  '&:active': {
    height: '14px',
  },
};

export type IKnockoutMobileProps = {
  knockoutGroupNames?: string[];
  maxSteps?: number;
  activeStep?: number;
  smallFinalName?: string;
  hasSmallFinal?: boolean;
  handleEventClick?: (id: string | number) => void;
  isMobile?: boolean;
  sport?: string;
  type?: string;
  isNational?: boolean;
} & (
  | { isPlayoff: false; knockoutGroupGames: (INormalizedKnockoutGroup[] | undefined)[] | undefined }
  | { isPlayoff: true; knockoutGroupGames: IPlayoffRoundGroup[][] }
);

export const KnockoutMobile = ({
  knockoutGroupNames,
  knockoutGroupGames,
  maxSteps,
  activeStep,
  smallFinalName = SMALL_FINAL,
  hasSmallFinal,
  handleEventClick,
  isMobile,
  isPlayoff,
  sport,
  type,
  isNational,
}: IKnockoutMobileProps): JSX.Element => {
  const [activeGroup, setActiveGroup] = useState<number>(activeStep || 0);
  const [hasOpenMenu, setHasOpenMenu] = useState(false);
  const slider = useRef<Slider | null>(null);
  const sliderContainer = useRef<HTMLDivElement | null>(null);

  const [pointerStart, setPointerStart] = useState<number | null>(null);
  const [pointerEnd, setPointerEnd] = useState<number | null>(null);

  const settings = useMemo(() => {
    return {
      dots: false,
      infinite: false,
      speed: 250,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      className: hasOpenMenu ? 'knockoutMobileSlickSliderWithMenu' : 'knockoutMobileSlickSlider',
      swipe: false,
      draggable: true,
      initialSlide: activeStep,
      afterChange: (current: number) => setActiveGroup(current),
    };
  }, [hasOpenMenu, activeStep, setActiveGroup]);

  const handlePointerDown = useCallback((e: PointerEvent) => {
    setPointerStart(e.clientX);
  }, []);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (pointerStart !== null) {
        setPointerEnd(e.clientX);
      }
    },
    [pointerStart],
  );

  const handlePointerUp = useCallback(() => {
    if (pointerStart === null || pointerEnd === null) return;
    const diff = pointerStart - pointerEnd;
    if (diff > 50) {
      slider.current?.slickNext();
    } else if (diff < -50) {
      slider.current?.slickPrev();
    }
    setPointerStart(null);
    setPointerEnd(null);
  }, [pointerStart, pointerEnd]);

  useEffect(() => {
    const currentContainer = sliderContainer.current;
    if (currentContainer) {
      currentContainer.addEventListener('pointerdown', handlePointerDown);
      currentContainer.addEventListener('pointermove', handlePointerMove);
      currentContainer.addEventListener('pointerup', handlePointerUp);
      currentContainer.addEventListener('pointercancel', handlePointerUp);

      return () => {
        currentContainer.removeEventListener('pointerdown', handlePointerDown);
        currentContainer.removeEventListener('pointermove', handlePointerMove);
        currentContainer.removeEventListener('pointerup', handlePointerUp);
        currentContainer.removeEventListener('pointercancel', handlePointerUp);
      };
    }
  }, [handlePointerDown, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    if (slider.current) {
      slider.current.slickGoTo(activeGroup);
    }
  }, [activeGroup]);

  const isPlayoffGroup = (group: INormalizedKnockoutGroup | IPlayoffRoundGroup): group is IPlayoffRoundGroup => {
    return isPlayoff;
  };

  return (
    <SMPKnockoutMobileContainer css={{ display: isMobile ? 'block' : 'none' }}>
      {knockoutGroupNames && (
        <div ref={sliderContainer} style={{ touchAction: 'pan-y' }}>
          <Slider ref={slider} {...settings}>
            {knockoutGroupNames.map((name, index) => {
              return (
                <div style={{ marginTop: '8px' }} key={name}>
                  <SMPFlex css={{ px: '$3', alignItems: 'center', position: 'relative' }}>
                    {activeGroup !== 0 && (
                      <div style={{ position: 'absolute', left: '30px', top: '15px' }}>
                        <SMPArrowLeftIcon
                          onClick={() => slider?.current?.slickPrev()}
                          css={{
                            ...arrowCss,
                          }}
                        />
                      </div>
                    )}
                    <KnockoutHead name={name} isMobile />
                    {maxSteps && activeGroup !== maxSteps - 1 && (
                      <div style={{ position: 'absolute', right: '30px', top: '15px' }}>
                        <SMPArrowRightIcon
                          onClick={() => slider?.current?.slickNext()}
                          css={{
                            ...arrowCss,
                          }}
                        />
                      </div>
                    )}
                  </SMPFlex>
                  <SMPFlex
                    css={{
                      marginTop: '20px',
                      display: 'grid',
                      justifyContent: 'normal',
                      paddingLeft: '$3',
                    }}
                  >
                    {knockoutGroupGames?.[index]?.map((group, idx) => {
                      const removeConnectingLines = knockoutGroupGames.length - 1 === index;

                      return (
                        <SMPKnockoutMobileGameContainer
                          smallFinal={hasSmallFinal === true && knockoutGroupNames.length - 2 === index}
                          removeConnectingLines={removeConnectingLines}
                          even={idx % 2 === 0}
                          odd={removeConnectingLines ? false : Math.abs(idx % 2) == 1}
                          firstGroup={index === 0}
                          doublesVariant={isPlayoffGroup(group) && isKnouckoutGameDoubles(type)}
                          variant={'group'}
                          key={group.id}
                        >
                          {knockoutGroupNames.length - 1 === index && index > 0 && hasSmallFinal && Math.abs(idx % 2) == 1 ? (
                            <SMPKnockoutSmallFinal>
                              <SMPText size='3' variant='contrast' css={{ textAlign: 'center' }}>
                                {smallFinalName}
                              </SMPText>
                            </SMPKnockoutSmallFinal>
                          ) : null}
                          <KnockoutRoundConnectingLine
                            lastGroupNameIndex={knockoutGroupNames.length - 1}
                            hasSmallFinal={hasSmallFinal}
                            index={index}
                            idx={idx}
                          />

                          {isPlayoffGroup(group) ? (
                            renderKnockoutGameType(group, handleEventClick, sport, type, isNational, name, setHasOpenMenu, isMobile)
                          ) : (
                            <KnockoutGame
                              handleEventClick={handleEventClick}
                              group={group}
                              isNational={isNational}
                              groupName={name}
                              sport={sport}
                            />
                          )}
                        </SMPKnockoutMobileGameContainer>
                      );
                    })}
                  </SMPFlex>
                </div>
              );
            })}
          </Slider>
        </div>
      )}
    </SMPKnockoutMobileContainer>
  );
};
